import { isUndefined } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';

import { ReportCategory } from '~hooks/useOmniReports/models';
import { useOmniReports } from '~hooks/useOmniReports/useOmniReports';

type ReportCategoryQueryState = {
  data: ReportCategory[];
  loading: boolean;
  cursor?: string;
  nextCursor?: string;
};

export type UseReportCategoryQueryResult = ReturnType<typeof useReportCategoryQuery>;

const useReportCategoryQuery = (companyId: string | null) => {
  const { getReportCategories } = useOmniReports();

  const [query, setQuery] = useState<ReportCategoryQueryState>({
    data: [],
    loading: true,
  });

  const hasMore = 'nextCursor' in query && !isUndefined(query.nextCursor);

  const fetchReportCategories = async (cursor?: string) => {
    if (!companyId) {
      return;
    }

    const response = await getReportCategories({
      companyId,
      limit: 20,
      cursor: cursor,
    });
    return response;
  };

  const fetchReportCategoriesPage = async () => {
    try {
      const response = await fetchReportCategories(query.cursor);

      if (!response) {
        setQuery((state) => ({
          ...state,
          data: [],
          loading: false,
        }));
        return;
      }

      setQuery((state) => ({
        ...state,
        loading: false,
        data: [...state.data, ...response.data],
        nextCursor: response.pagination.after,
      }));
    } catch (error) {
      console.error('Unable to fetch report categories', error);
    }
  };

  const fetchMore = useCallback(() => {
    if (query.loading || !hasMore) {
      return;
    }

    setQuery((state) => ({ ...state, loading: true, cursor: state.nextCursor }));
  }, [query.loading, hasMore]);

  const refetch = useCallback(() => {
    setQuery((state) => ({
      ...state,
      data: [],
      cursor: undefined,
      nextCursor: undefined,
      loading: true,
    }));
  }, []);

  useEffect(() => {
    if (query.loading) {
      fetchReportCategoriesPage();
    }
  }, [query.loading]);

  useEffect(() => {
    setQuery({
      data: [],
      loading: Boolean(companyId),
    });
  }, [companyId]);

  return {
    data: query.data,
    fetchMore,
    hasMore,
    loading: query.loading,
    refetch,
    setQuery,
  };
};

export default useReportCategoryQuery;
